export type ReportCoverPhotoMedia = {
  id?: string;
  url?: string | null;
  mimeType?: string | null;
};

export type ReportCoverPhotoObservation = {
  commentType?: string;
  media?: ReportCoverPhotoMedia[];
};

export type ReportCoverPhotoSubsection = {
  subSectionId?: string;
  order?: number;
  notPresent?: boolean;
  observations?: ReportCoverPhotoObservation[];
};

export type ReportCoverPhotoSection = {
  sectionId: string;
  label?: string;
  order: number;
  hiddenFromClient?: boolean;
  overviewMedia?: ReportCoverPhotoMedia[];
  subsections?: ReportCoverPhotoSubsection[];
};

export type ReportCoverPhotoPayload = {
  sections: ReportCoverPhotoSection[];
};

export type ResolveReportCoverPhotoInput = {
  coverPhoto?: unknown;
  imageUrl?: unknown;
  image?: unknown;
  portalImageUrl?: unknown;
  portalImage?: unknown;
  payload?: ReportCoverPhotoPayload | null;
  /**
   * Display-only Street View URL (workorder proxy). Used when no user cover
   * and no exterior overview still exist. Callers must not persist this.
   */
  streetViewUrl?: unknown;
};

const EXTERIOR_SECTION_TOKENS = new Set([
  'exterior',
  'grounds',
  'ground',
  'site',
  'sites',
  'roof',
  'roofing',
]);

export function trimCoverPhotoUrl(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Saved covers that are Street View assets are not a user photo.
 * Same check as the workorder (`cover_photo.includes('streetview')`).
 */
export function isPersistedStreetViewCover(url: string): boolean {
  return /streetview/i.test(url);
}

/**
 * Canonical Street View proxy path. Matches `callStreetView` / `/api/streetview`.
 * Do not add `size`, a timestamp, or a Google URL — the route caches one 600×600
 * image for 7 days and 301s a miss to `/no_image_found.png`.
 */
export function streetViewProxyPath(address: string): string {
  const loc = address.trim();
  if (!loc) return '';
  return `/api/streetview?address=${encodeURIComponent(loc)}`;
}

function sectionTokens(section: { sectionId: string; label?: string }): Set<string> {
  const raw = `${section.sectionId} ${section.label ?? ''}`;
  const parts = raw.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  return new Set(parts);
}

/** Whole-word tokens on section id and label; excludes attic. */
export function isExteriorReportSection(section: {
  sectionId: string;
  label?: string;
}): boolean {
  const tokens = sectionTokens(section);
  if (tokens.has('attic')) return false;
  for (const token of EXTERIOR_SECTION_TOKENS) {
    if (tokens.has(token)) return true;
  }
  return false;
}

export function isVideoCoverMedia(media: ReportCoverPhotoMedia): boolean {
  const url = trimCoverPhotoUrl(media.url);
  if (!url) return false;
  if (media.mimeType && /^video\//i.test(media.mimeType)) return true;
  return /\.(mp4|mov|webm|m4v)(\?|$)/i.test(url);
}

/**
 * Client report + PDF cover.
 * User cover, then exterior overview still, then display-only Street View.
 * Never writes `cover_photo`. Null only when Street View was not supplied.
 */
export function resolveReportCoverPhotoUrl(input: ResolveReportCoverPhotoInput): string | null {
  for (const candidate of [
    input.coverPhoto,
    input.imageUrl,
    input.image,
    input.portalImageUrl,
    input.portalImage,
  ]) {
    const url = trimCoverPhotoUrl(candidate);
    if (url && !isPersistedStreetViewCover(url)) return url;
  }

  const sections = input.payload?.sections ?? [];
  const ordered = [...sections].sort((a, b) => a.order - b.order);
  for (const section of ordered) {
    if (section.hiddenFromClient) continue;
    if (!isExteriorReportSection(section)) continue;
    for (const media of section.overviewMedia ?? []) {
      const url = trimCoverPhotoUrl(media.url);
      if (url && !isVideoCoverMedia(media)) return url;
    }
  }

  const streetViewUrl = trimCoverPhotoUrl(input.streetViewUrl);
  if (streetViewUrl) return streetViewUrl;

  return null;
}

/** Web legacy skin + PDF placeholder when no elevation photo is available. */
export const REPORT_COVER_NO_PHOTO_ICON_PATH = '/no_image_found.png';
